import { AfterViewInit, Directive, ElementRef, Injector, Renderer2, afterRenderEffect, inject, input, signal } from '@angular/core';

import { computedPrevious } from '@frontend/vanilla/core';
import { PLATFORM } from '@frontend/vanilla/ssr';

export enum ValueSlideAnimation {
    SlideFromTop = 'slide-from-top',
    HorizontalFlip = 'horizontal-flip',
}

@Directive({ selector: '[msValueSlider]' })
export class ValueSliderDirective implements AfterViewInit {
    private element = inject(ElementRef);
    private renderer = inject(Renderer2);
    private injector = inject(Injector);
    private platform = inject(PLATFORM);

    readonly value = input.required<string>({ alias: 'msValueSlider' });
    readonly animationClass = input<ValueSlideAnimation>(ValueSlideAnimation.SlideFromTop);

    /**
     * An optional input to stop adding 'slide-from-top' class when event changed through left navigation tree
     */
    readonly eventId = input<string>();

    private current: HTMLDivElement;
    private previous: HTMLDivElement;

    private readonly previousValue = computedPrevious(this.value);
    private readonly previousEventId = computedPrevious(this.eventId);

    private readonly divsRendered = signal(false);

    // NOTE: This shouldn't be put inside a computed as it will only animate once
    // because computed stores the last value and doesn't trigger the effect anymore
    private readonly shouldAnimate = () => {
        if (!this.value() || !this.divsRendered()) {
            return false;
        }

        return this.value() !== this.previousValue();
    };

    ngAfterViewInit(): void {
        this.creationPhase();

        afterRenderEffect(
            () => {
                // won't run on SSR
                this.updatePhase();
            },
            { injector: this.injector },
        );
    }

    private updatePhase() {
        const shouldAnimate = this.shouldAnimate();

        if (!shouldAnimate) {
            return;
        }

        const isSameEvent = !this.eventId() || this.eventId() === this.previousEventId();

        this.renderer.removeClass(this.current, this.animationClass());
        this.renderer.removeClass(this.previous, this.animationClass());

        setTimeout(() => {
            this.renderer.setProperty(this.current, 'textContent', this.value());
            this.renderer.setProperty(this.previous, 'textContent', this.previousValue());

            if (isSameEvent) {
                this.renderer.addClass(this.current, this.animationClass());
                this.renderer.addClass(this.previous, this.animationClass());
            }
        });
    }

    private creationPhase(): void {
        if (this.platform.isServerRendered) {
            const elements = [...this.element.nativeElement.querySelectorAll('div')];
            if (elements && elements.length > 0) {
                this.current = elements[0];
                this.previous = elements[1];
                this.divsRendered.set(true);
                return;
            }
        }

        this.current = this.createElement(this.value());
        this.previous = this.createElement(this.value(), true);

        this.renderer.appendChild(this.element.nativeElement, this.current);
        this.renderer.appendChild(this.element.nativeElement, this.previous);

        this.divsRendered.set(true);
    }

    private createElement(value: string, hide = false): HTMLDivElement {
        const el = this.renderer.createElement('div') as HTMLDivElement;

        this.renderer.setProperty(el, 'class', 'value-slider-value');

        this.renderer.setProperty(el, 'textContent', value);

        if (hide) {
            this.renderer.addClass(el, 'value-slider--hidden');
        }

        return el;
    }
}
